
// pages/Attendance.jsx

import {
    AlertCircle,
    Award,
    CheckCircle,
    Clock,
    Coffee,
    LogOut,
    MapPin,
    Navigation
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../components/common/Loading';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';
import {
    checkIn,
    checkOut,
    endBreak,
    getCurrentSessionDuration,
    getLocationName,
    getOrCreateTodayAttendance,
    getOvertimeStatus,
    requestOvertime,
    startBreak,
    startPeriodicLocationTracking
} from '../services/attendanceService';

const Attendance = () => {
    const { user, loading: isLoading } = useAuth();
    const navigate = useNavigate();
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [location, setLocation] = useState(null);
    const [locationName, setLocationName] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [sessionDuration, setSessionDuration] = useState(0);
    const [overtimeStatus, setOvertimeStatus] = useState(null);
    const [isTracking, setIsTracking] = useState(false);
    const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
    const [trackingCount, setTrackingCount] = useState(0);
    const stopTrackingRef = useRef(null);

    // Get user's location
    const getLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const loc = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        speed: position.coords.speed,
                        heading: position.coords.heading
                    };
                    setLocation(loc);

                    try {
                        const name = await getLocationName(loc.latitude, loc.longitude);
                        setLocationName(name);
                    } catch (error) {
                        console.warn('Could not get location name:', error);
                    }
                },
                (error) => {
                    console.warn('Location permission denied:', error);
                    fetch('https://ipapi.co/json/')
                        .then(res => res.json())
                        .then(data => {
                            const loc = {
                                latitude: data.latitude,
                                longitude: data.longitude,
                                city: data.city,
                                country: data.country_name
                            };
                            setLocation(loc);
                            setLocationName(`${data.city}, ${data.country_name}`);
                        })
                        .catch(() => { });
                },
                {
                    enableHighAccuracy: true,
                    timeout: 30000,
                    maximumAge: 60000
                }
            );
        }
    };

    // Load attendance data
    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            try {
                const data = await getOrCreateTodayAttendance(user);
                setAttendance(data);

                // Check overtime status
                const otStatus = await getOvertimeStatus(user.uid);
                setOvertimeStatus(otStatus);

                getLocation();
            } catch (error) {
                console.error('Error loading attendance:', error);
                toast.error('Failed to load attendance data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user]);

    // Update current time and session duration every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
            if (attendance) {
                const duration = getCurrentSessionDuration(attendance);
                setSessionDuration(duration);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [attendance]);

    // Start tracking when checked in
    useEffect(() => {
        if (isCheckedIn && !isTracking && user) {
            // Auto-start tracking when checked in
            startTracking();
        }

        // Stop tracking when checked out
        if (isCheckedOut && isTracking) {
            stopTracking();
        }

        return () => {
            if (stopTrackingRef.current) {
                stopTrackingRef.current();
            }
        };
    }, [attendance?.status, user]);

    // Check if user has active session
    const hasActiveSession = () => {
        if (!attendance?.sessions) return false;
        return attendance.sessions.some(s => !s.checkOut);
    };

    // Get active session
    const getActiveSession = () => {
        if (!attendance?.sessions) return null;
        return attendance.sessions.find(s => !s.checkOut);
    };

    // Start location tracking
    const startTracking = () => {
        if (!user) return;

        if (stopTrackingRef.current) {
            stopTrackingRef.current();
            stopTrackingRef.current = null;
        }

        toast.success('📍 Location tracking started. Updates every 5 minutes.');
        setIsTracking(true);
        setTrackingCount(0);

        const stopTracking = startPeriodicLocationTracking(user.uid, (location) => {
            setLastLocationUpdate(location);
            setTrackingCount(prev => prev + 1);
            console.log('📍 Location updated:', location);
        });

        stopTrackingRef.current = stopTracking;
    };

    // Stop location tracking
    const stopTracking = () => {
        if (stopTrackingRef.current) {
            stopTrackingRef.current();
            stopTrackingRef.current = null;
        }
        setIsTracking(false);
        toast.custom('📍 Location tracking stopped.');
    };

    // Toggle tracking
    const toggleTracking = () => {
        if (isTracking) {
            stopTracking();
        } else {
            startTracking();
        }
    };

    // Handle check-in
    const handleCheckIn = async () => {
        if (!user) return;
        setActionLoading(true);
        try {
            const result = await checkIn(
                user.uid,
                location,
                navigator.userAgent
            );
            setAttendance(result);
            toast.success('✅ Checked in successfully!');

            // Auto-start tracking after check-in
            setTimeout(() => {
                startTracking();
            }, 1000);
        } catch (error) {
            toast.error(error.message || 'Failed to check in');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle break start
    const handleStartBreak = async () => {
        if (!user) return;
        setActionLoading(true);
        try {
            const result = await startBreak(user.uid);
            setAttendance(result);
            toast.success('☕ Break started!');
            // Pause tracking during break (optional)
            if (isTracking) {
                // We keep tracking running but could pause if needed
            }
        } catch (error) {
            toast.error(error.message || 'Failed to start break');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle break end
    const handleEndBreak = async () => {
        if (!user) return;
        setActionLoading(true);
        try {
            const result = await endBreak(user.uid);
            setAttendance(result);
            toast.success('✅ Break ended!');
        } catch (error) {
            toast.error(error.message || 'Failed to end break');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle check-out
    const handleCheckOut = async () => {
        if (!user) return;
        setActionLoading(true);
        try {
            const result = await checkOut(user.uid, location, false);
            setAttendance(result);
            toast.success('👋 Checked out successfully!');

            // Stop tracking on check-out
            stopTracking();

            // Show overtime notification
            if (result.overtime?.isOvertime) {
                toast.success(`⏰ Overtime: ${result.overtime.minutes} minutes recorded. Pending approval.`);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to check out');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle manual overtime request
    const handleRequestOvertime = async () => {
        if (!user) return;
        setActionLoading(true);
        try {
            const result = await requestOvertime(user.uid, attendance.date);
            setAttendance(result);
            setOvertimeStatus({ requested: true, pending: true });
            toast.success('⏰ Overtime request submitted! Waiting for admin approval.');
        } catch (error) {
            toast.error(error.message || 'Failed to request overtime');
        } finally {
            setActionLoading(false);
        }
    };

    const isCheckedIn = attendance?.status === 'checked-in';
    const isOnBreak = attendance?.status === 'on-break';
    const isCheckedOut = attendance?.status === 'checked-out';
    const isNotCheckedIn = attendance?.status === 'not-checked-in';
    const activeSession = getActiveSession();

    // Format duration
    const formatDuration = (minutes) => {
        if (!minutes) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    // Get status badge
    const StatusBadge = ({ status }) => {
        const configs = {
            'not-checked-in': { label: 'Not Checked In', color: 'bg-gray-100 text-gray-600' },
            'checked-in': { label: 'Checked In', color: 'bg-green-100 text-green-700' },
            'on-break': { label: 'On Break', color: 'bg-yellow-100 text-yellow-700' },
            'checked-out': { label: 'Checked Out', color: 'bg-blue-100 text-blue-700' }
        };
        const config = configs[status] || configs['not-checked-in'];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    if (loading || isLoading) return <Loading />;

    // Check if overtime can be requested
    const canRequestOvertime = () => {
        if (!attendance) return false;
        return isCheckedOut &&
            !attendance.overtime?.isOvertime &&
            !overtimeStatus?.pending;
    };

    return (
        <>
            <Navbar user={user} />
            <div className="max-w-4xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
                        <p className="text-sm text-gray-500">
                            {new Date().toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </p>
                    </div>
                    <StatusBadge status={attendance?.status} />
                </div>

                {/* Time Display */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-pink-100 rounded-xl">
                                <Clock className="w-6 h-6 text-pink-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Current Time</p>
                                <p className="text-3xl font-bold text-gray-800">
                                    {currentTime.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit'
                                    })}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-500">Session Duration</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {isCheckedIn || isOnBreak ? formatDuration(sessionDuration) : '--'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Actions */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {/* Check In Button */}
                    <button
                        onClick={handleCheckIn}
                        disabled={actionLoading || isCheckedIn || isOnBreak}
                        className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${(!isCheckedIn && !isOnBreak)
                            ? 'border-green-500 bg-green-50 hover:bg-green-100'
                            : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            }`}
                    >
                        <CheckCircle className={`w-8 h-8 ${(!isCheckedIn && !isOnBreak) ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium mt-2">Check In</span>
                        {isCheckedOut && (
                            <span className="text-xs text-green-600 mt-1">New Session</span>
                        )}
                    </button>

                    {/* Break Button */}
                    {isCheckedIn && (
                        <button
                            onClick={handleStartBreak}
                            disabled={actionLoading}
                            className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-yellow-500 bg-yellow-50 hover:bg-yellow-100 transition-all"
                        >
                            <Coffee className="w-8 h-8 text-yellow-600" />
                            <span className="text-sm font-medium mt-2">Take Break</span>
                        </button>
                    )}

                    {/* End Break Button */}
                    {isOnBreak && (
                        <button
                            onClick={handleEndBreak}
                            disabled={actionLoading}
                            className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-green-500 bg-green-50 hover:bg-green-100 transition-all"
                        >
                            <Coffee className="w-8 h-8 text-green-600" />
                            <span className="text-sm font-medium mt-2">End Break</span>
                        </button>
                    )}

                    {/* Check Out Button */}
                    {(isCheckedIn || isOnBreak) && (
                        <button
                            onClick={handleCheckOut}
                            disabled={actionLoading}
                            className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-red-500 bg-red-50 hover:bg-red-100 transition-all"
                        >
                            <LogOut className="w-8 h-8 text-red-600" />
                            <span className="text-sm font-medium mt-2">Check Out</span>
                        </button>
                    )}

                    {/* Location Tracking Button */}
                    {/* {isCheckedIn && (
                        <button
                            onClick={toggleTracking}
                            disabled={actionLoading}
                            className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${isTracking
                                ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                                : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                                }`}
                        >
                            <Navigation className={`w-8 h-8 ${isTracking ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className="text-sm font-medium mt-2">
                                {isTracking ? 'Tracking' : 'Start Track'}
                            </span>
                            {isTracking && (
                                <span className="text-xs text-blue-600 mt-1 animate-pulse">● Live</span>
                            )}
                            {isTracking && trackingCount > 0 && (
                                <span className="text-[10px] text-gray-400 mt-0.5">
                                    {trackingCount} updates
                                </span>
                            )}
                        </button>
                    )} */}
                </div>

                {/* Tracking Status */}
                {isTracking && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-blue-700">
                                Live location tracking active
                            </span>
                        </div>
                        <span className="text-xs text-blue-600">
                            {trackingCount} location updates
                        </span>
                    </div>
                )}

                {/* Session History */}
                {attendance?.sessions && attendance.sessions.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Today's Sessions ({attendance.sessions.length})
                        </h3>
                        <div className="space-y-3">
                            {attendance.sessions.map((session, index) => {
                                const isActive = !session.checkOut;
                                const checkInTime = session.checkIn?.time?.seconds
                                    ? new Date(session.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : '-';
                                const checkOutTime = session.checkOut?.time?.seconds
                                    ? new Date(session.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : 'Active';
                                const breakCount = session.breaks?.length || 0;
                                const breakTime = (session.breaks || [])
                                    .filter(b => b.end)
                                    .reduce((sum, b) => sum + (b.duration || 0), 0);

                                return (
                                    <div key={session.id} className={`p-3 rounded-lg border ${isActive ? 'border-green-200 bg-green-50' : 'border-gray-200'
                                        }`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <span className="font-medium text-gray-800">
                                                    Session #{index + 1}
                                                    {isActive && <span className="ml-2 text-xs text-green-600 font-normal">● Active</span>}
                                                    {session.isOvertime && (
                                                        <span className="ml-2 text-xs text-orange-600 font-normal">⏰ Overtime</span>
                                                    )}
                                                </span>
                                                <div className="text-sm text-gray-500 mt-1">
                                                    <span className="inline-flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {checkInTime} - {checkOutTime}
                                                        {session.duration > 0 && ` (${formatDuration(session.duration)})`}
                                                    </span>
                                                    {breakCount > 0 && (
                                                        <span className="ml-3 inline-flex items-center gap-1">
                                                            <Coffee className="w-3 h-3" />
                                                            {breakCount} break(s), {breakTime}m total
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {!isActive && session.isOvertime && (
                                                <span className={`text-xs font-medium ${session.isOvertime?.approved ? 'text-green-600' : 'text-orange-600'
                                                    }`}>
                                                    {session.isOvertime?.approved ? '✓ Approved' : '⏳ Pending'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Status Details */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Today's Summary</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Total Sessions</span>
                            <span className="font-medium">{attendance?.sessions?.length || 0}</span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Total Work Duration</span>
                            <span className="font-medium text-blue-600">
                                {formatDuration(attendance?.totalWorkDuration || 0)}
                            </span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Total Break Time</span>
                            <span className="font-medium">{attendance?.totalBreakTime || 0}m</span>
                        </div>

                        {attendance?.overtime?.isOvertime && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-gray-600 flex items-center gap-2">
                                    <Award className="w-4 h-4 text-orange-500" />
                                    Total Overtime
                                </span>
                                <span className={`font-medium ${attendance.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
                                    {attendance.overtime.minutes}m
                                    {!attendance.overtime.approved && ' (Pending Approval)'}
                                </span>
                            </div>
                        )}

                        {/* Manual Overtime Request Button */}
                        {canRequestOvertime() && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleRequestOvertime}
                                    disabled={actionLoading}
                                    className="w-full py-2.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                                >
                                    <Award className="w-4 h-4" />
                                    {actionLoading ? 'Submitting...' : 'Request Overtime'}
                                </button>
                                <p className="text-xs text-gray-400 mt-1 text-center">
                                    Request overtime for today's work (will be reviewed by admin)
                                </p>
                            </div>
                        )}

                        {overtimeStatus?.pending && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                                    <span className="text-sm text-yellow-700">
                                        Overtime request pending admin approval
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Location Info */}
                {locationName && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                        <MapPin className="w-3 h-3" />
                        <span>{locationName}</span>
                    </div>
                )}
            </div>
        </>
    );
};

export default Attendance;

// // pages/Attendance.jsx

// import {
//     Award,
//     CheckCircle,
//     Clock,
//     Coffee,
//     LogOut,
//     MapPin,
//     Plus,
//     RefreshCw,
//     AlertCircle
// } from 'lucide-react';
// import { useEffect, useState } from 'react';
// import toast from 'react-hot-toast';
// import { useNavigate } from 'react-router-dom';
// import { Loading } from '../components/common/Loading';
// import Navbar from '../components/header/Navbar';
// import { useAuth } from '../hooks/useAuth';
// import {
//     checkIn,
//     checkOut,
//     endBreak,
//     getOrCreateTodayAttendance,
//     startBreak,
//     getCurrentSessionDuration,
//     getLocationName,
//     requestOvertime,
//     getOvertimeStatus
// } from '../services/attendanceService';

// const Attendance = () => {
//     const { user, loading: isLoading } = useAuth();
//     const navigate = useNavigate();
//     const [attendance, setAttendance] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [actionLoading, setActionLoading] = useState(false);
//     const [location, setLocation] = useState(null);
//     const [locationName, setLocationName] = useState('');
//     const [currentTime, setCurrentTime] = useState(new Date());
//     const [sessionDuration, setSessionDuration] = useState(0);
//     const [overtimeStatus, setOvertimeStatus] = useState(null);

//     // Get user's location
//     const getLocation = () => {
//         if (navigator.geolocation) {
//             navigator.geolocation.getCurrentPosition(
//                 async (position) => {
//                     const loc = {
//                         latitude: position.coords.latitude,
//                         longitude: position.coords.longitude,
//                     };
//                     setLocation(loc);

//                     try {
//                         const name = await getLocationName(loc.latitude, loc.longitude);
//                         setLocationName(name);
//                     } catch (error) {
//                         console.warn('Could not get location name:', error);
//                     }
//                 },
//                 (error) => {
//                     console.warn('Location permission denied:', error);
//                     fetch('https://ipapi.co/json/')
//                         .then(res => res.json())
//                         .then(data => {
//                             const loc = {
//                                 latitude: data.latitude,
//                                 longitude: data.longitude,
//                                 city: data.city,
//                                 country: data.country_name
//                             };
//                             setLocation(loc);
//                             setLocationName(`${data.city}, ${data.country_name}`);
//                         })
//                         .catch(() => { });
//                 }
//             );
//         }
//     };

//     // Load attendance data
//     useEffect(() => {
//         if (!user) return;

//         const loadData = async () => {
//             try {
//                 const data = await getOrCreateTodayAttendance(user);
//                 setAttendance(data);

//                 // Check overtime status
//                 const otStatus = await getOvertimeStatus(user.uid);
//                 setOvertimeStatus(otStatus);

//                 getLocation();
//             } catch (error) {
//                 console.error('Error loading attendance:', error);
//                 toast.error('Failed to load attendance data');
//             } finally {
//                 setLoading(false);
//             }
//         };

//         loadData();
//     }, [user]);

//     // Update current time and session duration every second
//     useEffect(() => {
//         const interval = setInterval(() => {
//             setCurrentTime(new Date());
//             if (attendance) {
//                 const duration = getCurrentSessionDuration(attendance);
//                 setSessionDuration(duration);
//             }
//         }, 1000);
//         return () => clearInterval(interval);
//     }, [attendance]);

//     // Check if user has active session
//     const hasActiveSession = () => {
//         if (!attendance?.sessions) return false;
//         return attendance.sessions.some(s => !s.checkOut);
//     };

//     // Get active session
//     const getActiveSession = () => {
//         if (!attendance?.sessions) return null;
//         return attendance.sessions.find(s => !s.checkOut);
//     };

//     // Handle check-in
//     const handleCheckIn = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await checkIn(
//                 user.uid,
//                 location,
//                 navigator.userAgent
//             );
//             setAttendance(result);
//             toast.success('✅ Checked in successfully!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to check in');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle break start
//     const handleStartBreak = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await startBreak(user.uid);
//             setAttendance(result);
//             toast.success('☕ Break started!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to start break');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle break end
//     const handleEndBreak = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await endBreak(user.uid);
//             setAttendance(result);
//             toast.success('✅ Break ended!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to end break');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle check-out
//     const handleCheckOut = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await checkOut(user.uid, location, false);
//             setAttendance(result);
//             toast.success('👋 Checked out successfully!');

//             // Show overtime notification
//             if (result.overtime?.isOvertime) {
//                 toast.success(`⏰ Overtime: ${result.overtime.minutes} minutes recorded. Pending approval.`);
//             }
//         } catch (error) {
//             toast.error(error.message || 'Failed to check out');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle manual overtime request
//     const handleRequestOvertime = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await requestOvertime(user.uid, attendance.date);
//             setAttendance(result);
//             setOvertimeStatus({ requested: true, pending: true });
//             toast.success('⏰ Overtime request submitted! Waiting for admin approval.');
//         } catch (error) {
//             toast.error(error.message || 'Failed to request overtime');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     const isCheckedIn = attendance?.status === 'checked-in';
//     const isOnBreak = attendance?.status === 'on-break';
//     const isCheckedOut = attendance?.status === 'checked-out';
//     const isNotCheckedIn = attendance?.status === 'not-checked-in';
//     const activeSession = getActiveSession();

//     // Format duration
//     const formatDuration = (minutes) => {
//         if (!minutes) return '0h 0m';
//         const hours = Math.floor(minutes / 60);
//         const mins = Math.floor(minutes % 60);
//         return `${hours}h ${mins}m`;
//     };

//     // Get status badge
//     const StatusBadge = ({ status }) => {
//         const configs = {
//             'not-checked-in': { label: 'Not Checked In', color: 'bg-gray-100 text-gray-600' },
//             'checked-in': { label: 'Checked In', color: 'bg-green-100 text-green-700' },
//             'on-break': { label: 'On Break', color: 'bg-yellow-100 text-yellow-700' },
//             'checked-out': { label: 'Checked Out', color: 'bg-blue-100 text-blue-700' }
//         };
//         const config = configs[status] || configs['not-checked-in'];
//         return (
//             <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
//                 {config.label}
//             </span>
//         );
//     };

//     if (loading || isLoading) return <Loading />;

//     // Check if overtime can be requested
//     const canRequestOvertime = () => {
//         if (!attendance) return false;
//         return isCheckedOut &&
//             !attendance.overtime?.isOvertime &&
//             !overtimeStatus?.pending;
//     };

//     return (
//         <>
//             <Navbar user={user} />
//             <div className="max-w-4xl mx-auto px-4 py-6">
//                 {/* Header */}
//                 <div className="flex items-center justify-between mb-6">
//                     <div>
//                         <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
//                         <p className="text-sm text-gray-500">
//                             {new Date().toLocaleDateString('en-US', {
//                                 weekday: 'long',
//                                 year: 'numeric',
//                                 month: 'long',
//                                 day: 'numeric'
//                             })}
//                         </p>
//                     </div>
//                     <StatusBadge status={attendance?.status} />
//                 </div>

//                 {/* Time Display */}
//                 <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
//                     <div className="flex items-center justify-between">
//                         <div className="flex items-center gap-3">
//                             <div className="p-3 bg-pink-100 rounded-xl">
//                                 <Clock className="w-6 h-6 text-pink-600" />
//                             </div>
//                             <div>
//                                 <p className="text-sm text-gray-500">Current Time</p>
//                                 <p className="text-3xl font-bold text-gray-800">
//                                     {currentTime.toLocaleTimeString('en-US', {
//                                         hour: '2-digit',
//                                         minute: '2-digit',
//                                         second: '2-digit'
//                                     })}
//                                 </p>
//                             </div>
//                         </div>
//                         <div className="text-right">
//                             <p className="text-sm text-gray-500">Session Duration</p>
//                             <p className="text-2xl font-bold text-blue-600">
//                                 {isCheckedIn || isOnBreak ? formatDuration(sessionDuration) : '--'}
//                             </p>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Main Actions */}
//                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {/* Check In Button */}
//                     <button
//                         onClick={handleCheckIn}
//                         disabled={actionLoading || isCheckedIn || isOnBreak}
//                         className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${(!isCheckedIn && !isOnBreak)
//                             ? 'border-green-500 bg-green-50 hover:bg-green-100'
//                             : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
//                             }`}
//                     >
//                         <CheckCircle className={`w-8 h-8 ${(!isCheckedIn && !isOnBreak) ? 'text-green-600' : 'text-gray-400'}`} />
//                         <span className="text-sm font-medium mt-2">Check In</span>
//                         {isCheckedOut && (
//                             <span className="text-xs text-green-600 mt-1">New Session</span>
//                         )}
//                     </button>

//                     {/* Break Button */}
//                     {isCheckedIn && (
//                         <button
//                             onClick={handleStartBreak}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-yellow-500 bg-yellow-50 hover:bg-yellow-100 transition-all"
//                         >
//                             <Coffee className="w-8 h-8 text-yellow-600" />
//                             <span className="text-sm font-medium mt-2">Take Break</span>
//                         </button>
//                     )}

//                     {/* End Break Button */}
//                     {isOnBreak && (
//                         <button
//                             onClick={handleEndBreak}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-green-500 bg-green-50 hover:bg-green-100 transition-all"
//                         >
//                             <Coffee className="w-8 h-8 text-green-600" />
//                             <span className="text-sm font-medium mt-2">End Break</span>
//                         </button>
//                     )}

//                     {/* Check Out Button */}
//                     {(isCheckedIn || isOnBreak) && (
//                         <button
//                             onClick={handleCheckOut}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-red-500 bg-red-50 hover:bg-red-100 transition-all"
//                         >
//                             <LogOut className="w-8 h-8 text-red-600" />
//                             <span className="text-sm font-medium mt-2">Check Out</span>
//                         </button>
//                     )}
//                 </div>

//                 {/* Session History */}
//                 {attendance?.sessions && attendance.sessions.length > 0 && (
//                     <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
//                         <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
//                             <Clock className="w-5 h-5" />
//                             Today's Sessions ({attendance.sessions.length})
//                         </h3>
//                         <div className="space-y-3">
//                             {attendance.sessions.map((session, index) => {
//                                 const isActive = !session.checkOut;
//                                 const checkInTime = session.checkIn?.time?.seconds
//                                     ? new Date(session.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : '-';
//                                 const checkOutTime = session.checkOut?.time?.seconds
//                                     ? new Date(session.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : 'Active';
//                                 const breakCount = session.breaks?.length || 0;
//                                 const breakTime = (session.breaks || [])
//                                     .filter(b => b.end)
//                                     .reduce((sum, b) => sum + (b.duration || 0), 0);

//                                 return (
//                                     <div key={session.id} className={`p-3 rounded-lg border ${isActive ? 'border-green-200 bg-green-50' : 'border-gray-200'
//                                         }`}>
//                                         <div className="flex justify-between items-center">
//                                             <div>
//                                                 <span className="font-medium text-gray-800">
//                                                     Session #{index + 1}
//                                                     {isActive && <span className="ml-2 text-xs text-green-600 font-normal">● Active</span>}
//                                                     {session.isOvertime && (
//                                                         <span className="ml-2 text-xs text-orange-600 font-normal">⏰ Overtime</span>
//                                                     )}
//                                                 </span>
//                                                 <div className="text-sm text-gray-500 mt-1">
//                                                     <span className="inline-flex items-center gap-1">
//                                                         <Clock className="w-3 h-3" />
//                                                         {checkInTime} - {checkOutTime}
//                                                         {session.duration > 0 && ` (${formatDuration(session.duration)})`}
//                                                     </span>
//                                                     {breakCount > 0 && (
//                                                         <span className="ml-3 inline-flex items-center gap-1">
//                                                             <Coffee className="w-3 h-3" />
//                                                             {breakCount} break(s), {breakTime}m total
//                                                         </span>
//                                                     )}
//                                                 </div>
//                                             </div>
//                                             {!isActive && session.isOvertime && (
//                                                 <span className={`text-xs font-medium ${session.isOvertime?.approved ? 'text-green-600' : 'text-orange-600'
//                                                     }`}>
//                                                     {session.isOvertime?.approved ? '✓ Approved' : '⏳ Pending'}
//                                                 </span>
//                                             )}
//                                         </div>
//                                     </div>
//                                 );
//                             })}
//                         </div>
//                     </div>
//                 )}

//                 {/* Status Details */}
//                 <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
//                     <h3 className="font-semibold text-gray-800 mb-4">Today's Summary</h3>
//                     <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                             <span className="text-gray-600">Total Sessions</span>
//                             <span className="font-medium">{attendance?.sessions?.length || 0}</span>
//                         </div>

//                         <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                             <span className="text-gray-600">Total Work Duration</span>
//                             <span className="font-medium text-blue-600">
//                                 {formatDuration(attendance?.totalWorkDuration || 0)}
//                             </span>
//                         </div>

//                         <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                             <span className="text-gray-600">Total Break Time</span>
//                             <span className="font-medium">{attendance?.totalBreakTime || 0}m</span>
//                         </div>

//                         {attendance?.overtime?.isOvertime && (
//                             <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                                 <span className="text-gray-600 flex items-center gap-2">
//                                     <Award className="w-4 h-4 text-orange-500" />
//                                     Total Overtime
//                                 </span>
//                                 <span className={`font-medium ${attendance.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
//                                     {attendance.overtime.minutes}m
//                                     {!attendance.overtime.approved && ' (Pending Approval)'}
//                                 </span>
//                             </div>
//                         )}

//                         {/* Manual Overtime Request Button */}
//                         {canRequestOvertime() && (
//                             <div className="mt-4 pt-4 border-t border-gray-200">
//                                 <button
//                                     onClick={handleRequestOvertime}
//                                     disabled={actionLoading}
//                                     className="w-full py-2.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition flex items-center justify-center gap-2 font-medium disabled:opacity-50"
//                                 >
//                                     <Award className="w-4 h-4" />
//                                     {actionLoading ? 'Submitting...' : 'Request Overtime'}
//                                 </button>
//                                 <p className="text-xs text-gray-400 mt-1 text-center">
//                                     Request overtime for today's work (will be reviewed by admin)
//                                 </p>
//                             </div>
//                         )}

//                         {overtimeStatus?.pending && (
//                             <div className="mt-4 pt-4 border-t border-gray-200">
//                                 <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
//                                     <AlertCircle className="w-5 h-5 text-yellow-600" />
//                                     <span className="text-sm text-yellow-700">
//                                         Overtime request pending admin approval
//                                     </span>
//                                 </div>
//                             </div>
//                         )}
//                     </div>
//                 </div>

//                 {/* Location Info */}
//                 {locationName && (
//                     <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
//                         <MapPin className="w-3 h-3" />
//                         <span>{locationName}</span>
//                     </div>
//                 )}
//             </div>
//         </>
//     );
// };

// export default Attendance;

// // pages/Attendance.jsx

// import {
//     Award,
//     CheckCircle,
//     Clock,
//     Coffee,
//     LogOut,
//     MapPin
// } from 'lucide-react';
// import { useEffect, useState } from 'react';
// import toast from 'react-hot-toast';
// import { useNavigate } from 'react-router-dom';
// import { Loading } from '../components/common/Loading';
// import Navbar from '../components/header/Navbar';
// import { useAuth } from '../hooks/useAuth';
// import {
//     checkIn,
//     checkOut,
//     endBreak,
//     getOrCreateTodayAttendance,
//     startBreak
// } from '../services/attendanceService';

// const Attendance = () => {
//     const { user, loading: isLoading } = useAuth();
//     const navigate = useNavigate();
//     const [attendance, setAttendance] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [actionLoading, setActionLoading] = useState(false);
//     const [location, setLocation] = useState(null);
//     const [currentTime, setCurrentTime] = useState(new Date());

//     // Get user's location
//     const getLocation = () => {
//         if (navigator.geolocation) {
//             navigator.geolocation.getCurrentPosition(
//                 (position) => {
//                     setLocation({
//                         latitude: position.coords.latitude,
//                         longitude: position.coords.longitude,
//                     });
//                 },
//                 (error) => {
//                     console.warn('Location permission denied:', error);
//                     // Use IP-based location fallback
//                     fetch('https://ipapi.co/json/')
//                         .then(res => res.json())
//                         .then(data => {
//                             setLocation({
//                                 latitude: data.latitude,
//                                 longitude: data.longitude,
//                                 city: data.city,
//                                 country: data.country_name
//                             });
//                         })
//                         .catch(() => {
//                             // Silent fail - we'll still allow check-in without location
//                         });
//                 }
//             );
//         }
//     };

//     // Load attendance data
//     useEffect(() => {
//         if (!user) return;

//         const loadData = async () => {
//             try {
//                 const data = await getOrCreateTodayAttendance(user);
//                 setAttendance(data);
//                 getLocation();
//             } catch (error) {
//                 console.error('Error loading attendance:', error);
//                 toast.error('Failed to load attendance data');
//             } finally {
//                 setLoading(false);
//             }
//         };

//         loadData();
//     }, [user]);

//     // Update current time every second
//     useEffect(() => {
//         const interval = setInterval(() => {
//             setCurrentTime(new Date());
//         }, 1000);
//         return () => clearInterval(interval);
//     }, []);

//     // Handle check-in
//     const handleCheckIn = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await checkIn(
//                 user.uid,
//                 location,
//                 navigator.userAgent
//             );
//             setAttendance(result);
//             toast.success('✅ Checked in successfully!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to check in');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle break start
//     const handleStartBreak = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await startBreak(user.uid);
//             setAttendance(result);
//             toast.success('☕ Break started!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to start break');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle break end
//     const handleEndBreak = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await endBreak(user.uid);
//             setAttendance(result);
//             toast.success('✅ Break ended!');
//         } catch (error) {
//             toast.error(error.message || 'Failed to end break');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle check-out
//     const handleCheckOut = async () => {
//         if (!user) return;
//         setActionLoading(true);
//         try {
//             const result = await checkOut(user.uid, location, false);
//             setAttendance(result);
//             toast.success('👋 Checked out successfully!');

//             // Show overtime notification
//             if (result.overtime?.isOvertime) {
//                 toast.success(`⏰ Overtime: ${result.overtime.minutes} minutes recorded. Pending approval.`);
//             }
//         } catch (error) {
//             toast.error(error.message || 'Failed to check out');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     const isCheckedIn = attendance?.status === 'checked-in';
//     const isOnBreak = attendance?.status === 'on-break';
//     const isCheckedOut = attendance?.status === 'checked-out';
//     const isNotCheckedIn = attendance?.status === 'not-checked-in' || !attendance?.checkIn;

//     // Calculate hours worked
//     const getHoursWorked = () => {
//         if (!attendance?.checkIn) return '0h 0m';

//         const checkInTime = attendance.checkIn.time.seconds * 1000;
//         const now = Date.now();
//         const totalMinutes = (now - checkInTime) / 60000;
//         const breakMinutes = attendance.totalBreakTime || 0;
//         const workMinutes = Math.max(0, totalMinutes - breakMinutes);

//         const hours = Math.floor(workMinutes / 60);
//         const minutes = Math.floor(workMinutes % 60);
//         return `${hours}h ${minutes}m`;
//     };

//     // Get current break duration
//     const getBreakDuration = () => {
//         if (!attendance?.breaks) return '0m';
//         const breaks = attendance.breaks;
//         const lastBreak = breaks[breaks.length - 1];
//         if (lastBreak && !lastBreak.end) {
//             const start = lastBreak.start.seconds * 1000;
//             const now = Date.now();
//             const minutes = Math.floor((now - start) / 60000);
//             return `${minutes}m`;
//         }
//         return '0m';
//     };

//     // Get status badge
//     const StatusBadge = ({ status }) => {
//         const configs = {
//             'not-checked-in': { label: 'Not Checked In', color: 'bg-gray-100 text-gray-600' },
//             'checked-in': { label: 'Checked In', color: 'bg-green-100 text-green-700' },
//             'on-break': { label: 'On Break', color: 'bg-yellow-100 text-yellow-700' },
//             'checked-out': { label: 'Checked Out', color: 'bg-blue-100 text-blue-700' }
//         };
//         const config = configs[status] || configs['not-checked-in'];
//         return (
//             <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
//                 {config.label}
//             </span>
//         );
//     };

//     if (loading || isLoading) return <Loading />;

//     return (
//         <>
//             <Navbar user={user} />
//             <div className="max-w-4xl mx-auto px-4 py-6">
//                 {/* Header */}
//                 <div className="flex items-center justify-between mb-6">
//                     <div>
//                         <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
//                         <p className="text-sm text-gray-500">
//                             {new Date().toLocaleDateString('en-US', {
//                                 weekday: 'long',
//                                 year: 'numeric',
//                                 month: 'long',
//                                 day: 'numeric'
//                             })}
//                         </p>
//                     </div>
//                     <StatusBadge status={attendance?.status} />
//                 </div>

//                 {/* Time Display */}
//                 <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
//                     <div className="flex items-center justify-between">
//                         <div className="flex items-center gap-3">
//                             <div className="p-3 bg-pink-100 rounded-xl">
//                                 <Clock className="w-6 h-6 text-pink-600" />
//                             </div>
//                             <div>
//                                 <p className="text-sm text-gray-500">Current Time</p>
//                                 <p className="text-3xl font-bold text-gray-800">
//                                     {currentTime.toLocaleTimeString('en-US', {
//                                         hour: '2-digit',
//                                         minute: '2-digit',
//                                         second: '2-digit'
//                                     })}
//                                 </p>
//                             </div>
//                         </div>
//                         <div className="text-right">
//                             <p className="text-sm text-gray-500">Work Duration</p>
//                             <p className="text-2xl font-bold text-blue-600">{getHoursWorked()}</p>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Main Actions */}
//                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {/* Check In Button */}
//                     <button
//                         onClick={handleCheckIn}
//                         disabled={actionLoading || isCheckedIn || isOnBreak || isCheckedOut}
//                         className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${isNotCheckedIn
//                             ? 'border-green-500 bg-green-50 hover:bg-green-100'
//                             : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
//                             }`}
//                     >
//                         <CheckCircle className={`w-8 h-8 ${isNotCheckedIn ? 'text-green-600' : 'text-gray-400'}`} />
//                         <span className="text-sm font-medium mt-2">Check In</span>
//                     </button>

//                     {/* Break Button */}
//                     {isCheckedIn && (
//                         <button
//                             onClick={handleStartBreak}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-yellow-500 bg-yellow-50 hover:bg-yellow-100 transition-all"
//                         >
//                             <Coffee className="w-8 h-8 text-yellow-600" />
//                             <span className="text-sm font-medium mt-2">Take Break</span>
//                         </button>
//                     )}

//                     {/* End Break Button */}
//                     {isOnBreak && (
//                         <button
//                             onClick={handleEndBreak}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-green-500 bg-green-50 hover:bg-green-100 transition-all"
//                         >
//                             <Coffee className="w-8 h-8 text-green-600" />
//                             <span className="text-sm font-medium mt-2">End Break</span>
//                         </button>
//                     )}

//                     {/* Check Out Button */}
//                     {(isCheckedIn || isOnBreak) && (
//                         <button
//                             onClick={handleCheckOut}
//                             disabled={actionLoading}
//                             className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-red-500 bg-red-50 hover:bg-red-100 transition-all"
//                         >
//                             <LogOut className="w-8 h-8 text-red-600" />
//                             <span className="text-sm font-medium mt-2">Check Out</span>
//                         </button>
//                     )}
//                 </div>

//                 {/* Status Details */}
//                 <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
//                     <h3 className="font-semibold text-gray-800 mb-4">Today's Details</h3>
//                     <div className="space-y-3">
//                         {attendance?.checkIn && (
//                             <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                                 <span className="text-gray-600">Check In</span>
//                                 <span className="font-medium">
//                                     {new Date(attendance.checkIn.time.seconds * 1000).toLocaleTimeString()}
//                                 </span>
//                             </div>
//                         )}

//                         {attendance?.breaks && attendance.breaks.length > 0 && (
//                             <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                                 <span className="text-gray-600">Total Break</span>
//                                 <span className="font-medium">{attendance.totalBreakTime || 0}m</span>
//                             </div>
//                         )}

//                         {isOnBreak && (
//                             <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                                 <span className="text-gray-600">Current Break Duration</span>
//                                 <span className="font-medium text-yellow-600">{getBreakDuration()}</span>
//                             </div>
//                         )}

//                         <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                             <span className="text-gray-600">Work Duration</span>
//                             <span className="font-medium text-blue-600">{getHoursWorked()}</span>
//                         </div>

//                         {attendance?.checkOut && (
//                             <div className="flex justify-between items-center py-2 border-b border-gray-100">
//                                 <span className="text-gray-600">Check Out</span>
//                                 <span className="font-medium">
//                                     {new Date(attendance.checkOut.time.seconds * 1000).toLocaleTimeString()}
//                                 </span>
//                             </div>
//                         )}

//                         {attendance?.overtime?.isOvertime && (
//                             <div className="flex justify-between items-center py-2">
//                                 <span className="text-gray-600 flex items-center gap-2">
//                                     <Award className="w-4 h-4 text-orange-500" />
//                                     Overtime
//                                 </span>
//                                 <span className={`font-medium ${attendance.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
//                                     {attendance.overtime.minutes}m
//                                     {!attendance.overtime.approved && ' (Pending Approval)'}
//                                 </span>
//                             </div>
//                         )}
//                     </div>
//                 </div>

//                 {/* Location Info */}
//                 {location && (
//                     <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
//                         <MapPin className="w-3 h-3" />
//                         <span>
//                             {location.city || location.latitude?.toFixed(4) || 'Unknown'},
//                             {location.country || 'Location'}
//                         </span>
//                     </div>
//                 )}
//             </div>
//         </>
//     );
// };

// export default Attendance;